import { isEventHost } from './identity';

/**
 * Guest song-request cooldown. Applies to every guest queue append regardless
 * of where the song came from (search or an imported playlist).
 */
export const GUEST_REQUEST_COOLDOWN_MS = 30 * 1000;

/** Milliseconds still to wait, or 0 when a request is allowed. */
export function guestCooldownRemainingMs(lastRequestAt, now = Date.now(), cooldownMs = GUEST_REQUEST_COOLDOWN_MS) {
  const last = Number(lastRequestAt) || 0;
  if (!last) return 0;
  const elapsed = now - last;
  return elapsed >= cooldownMs ? 0 : cooldownMs - elapsed;
}

export function formatCooldownRemaining(remainingMs) {
  const seconds = Math.max(1, Math.ceil((Number(remainingMs) || 0) / 1000));
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * Deterministic queue-item id.
 *
 * Derived from fields the item already carries so re-normalizing the same item
 * (the store normalizes, then the publisher normalizes again) always yields the
 * same value. `id` stays the video id, which duplicate detection and the player
 * both rely on.
 */
export function buildQueueItemId({ videoId, addedBy, addedAt }) {
  return [videoId || 'unknown', addedBy || 'anon', addedAt || ''].join('::');
}

export function normalizeQueueItem(video, uid) {
  if (!video?.id) return null;

  const addedBy = video.addedBy || uid || null;
  const addedAt = video.addedAt || new Date().toISOString();

  return {
    id: video.id,
    title: video.title || '',
    artist: video.artist || video.channelTitle || '',
    thumbnail: video.thumbnail || '',
    duration: video.duration || '',
    views: video.views ?? video.viewCount ?? '',
    addedBy,
    addedAt,
    // Stable identifiers and provenance for later instrumentation. No behavior
    // depends on them yet.
    queueItemId: video.queueItemId || buildQueueItemId({ videoId: video.id, addedBy, addedAt }),
    videoId: video.videoId || video.id,
    source: video.source || 'search',
    // Canonical catalog identity when the caller already resolved one.
    recordingId: typeof video.recordingId === 'string' ? video.recordingId : null,
    providerAssetKey: typeof video.providerAssetKey === 'string' ? video.providerAssetKey : null,
    durationMs: Number.isFinite(Number(video.durationMs)) ? Number(video.durationMs) : null,
  };
}

export function shouldApplyQueueRevision(remoteRevision, lastAppliedRevision) {
  return Number(remoteRevision) > Number(lastAppliedRevision ?? -1);
}

export function commitQueueAppend(doc, item) {
  const items = Array.isArray(doc?.items) ? doc.items : [];
  const revision = Number(doc?.revision) || 0;
  if (!item?.id) {
    return { ok: false, reason: 'invalid', doc };
  }
  if (items.some((song) => song.id === item.id)) {
    return { ok: false, reason: 'duplicate', doc };
  }
  return {
    ok: true,
    doc: {
      items: [...items, item],
      revision: revision + 1,
    },
  };
}

export function commitQueueRemove(doc, index) {
  const items = Array.isArray(doc?.items) ? [...doc.items] : [];
  const revision = Number(doc?.revision) || 0;
  if (index < 0 || index >= items.length) {
    return { ok: false, reason: 'invalid-index', doc };
  }
  items.splice(index, 1);
  return {
    ok: true,
    doc: { items, revision: revision + 1 },
  };
}

export function commitQueueReorder(doc, fromIndex, toIndex) {
  const items = Array.isArray(doc?.items) ? [...doc.items] : [];
  const revision = Number(doc?.revision) || 0;
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return { ok: false, reason: 'invalid-index', doc };
  }
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return {
    ok: true,
    doc: { items, revision: revision + 1 },
  };
}

/** Simulates Firestore transaction retries: second writer re-reads after the first commit. */
export function simulateConcurrentAppends(initialDoc, itemA, itemB) {
  const first = commitQueueAppend(initialDoc, itemA);
  if (!first.ok) return first;
  const second = commitQueueAppend(first.doc, itemB);
  return second;
}

export function canAppendToQueue({ userId, hostId, guestRequestsEnabled, eventLive = true }) {
  if (eventLive === false) return false;
  if (isEventHost(userId, hostId)) return true;
  return guestRequestsEnabled === true;
}

export function canRemoveOrReorderQueue({ userId, hostId, eventLive = true }) {
  if (eventLive === false) return false;
  return isEventHost(userId, hostId);
}

export function canPublishSettings({ userId, hostId, eventLive = true }) {
  if (eventLive === false) return false;
  return isEventHost(userId, hostId);
}

export function needsSchemaV2(eventData, queueExists, playerExists) {
  return eventData?.schemaVersion !== 2 || !queueExists || !playerExists;
}

export function createEmptyQueueDoc(uid, items = []) {
  return {
    items,
    revision: items.length > 0 ? 1 : 0,
    updatedBy: uid || null,
  };
}

export function createEmptyPlayerDoc() {
  return {
    song: null,
    isPlaying: false,
    positionSeconds: 0,
    updatedAtMs: 0,
    version: 1,
    reason: 'song',
    updatedBy: null,
  };
}

export function isGuestAppendOnly(previousItems, nextItems) {
  if (!Array.isArray(previousItems) || !Array.isArray(nextItems)) return false;
  if (nextItems.length !== previousItems.length + 1) return false;
  return previousItems.every((item, index) => item.id === nextItems[index]?.id);
}
